// apps/web/src/widgets/static-note/renderer.tsx
//
// Phase 6 — StaticNoteRenderer (WIDG-01 / WIDG-02; D-12 catalog entry; D-14
// schema). D-13 docs-walkthrough target — kept simple so contributors can
// trace the seven-step recipe in `docs/ADD-A-TEMPLATE.md`.
//
// XSS defense layers (UI-SPEC §"Sanitization & Security Contract"):
//   - react-markdown v9 disables raw HTML pass-through by default
//   - ALLOWED_ELEMENTS prunes the AST to only the documented tag set
//   - COMPONENTS_ALLOWLIST provides safe renderer overrides
//   - safeUrlTransform rejects javascript:/data:/file: URL schemes
//   - inline <MarkdownErrorBoundary> renders the "Note couldn't render"
//     fallback on parse / render-time exceptions
//
// NO `dangerouslySetInnerHTML` anywhere — Plan 10 enforces globally.
//
// Phase 11.6 Plan 06 (D2 / Surface 3) — OPTIONAL visibility gating. When
// `config.visibilityConstraintId` is set, the note renders ONLY when the LIFTED
// `isVisibleById(id, recipe, states)` (Plan 03, `../visible-when.js`) returns
// true; when ABSENT the note renders unconditionally (the D-13 docs-walkthrough
// simple path is unchanged). The states map is read from `RecipeStateContext`
// because the gating constraint's `when.ref` points at a DIFFERENT widget
// (PATTERNS DR-2 / Pitfall 4) — the note's OWN `state` is irrelevant to the
// gate. Gating is fail-OPEN by construction (isVisibleById returns true on
// undefined/unresolved/wrong-kind), so a drifted constraint id SHOWS the
// safety warning rather than hiding it.
import { AlertTriangle, Info, StickyNote } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { RecipeStateContext } from "../recipe-state-provider.js";
import { isVisibleById } from "../visible-when.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { ALLOWED_ELEMENTS, COMPONENTS_ALLOWLIST, safeUrlTransform } from "./markdown-allowlist.js";

interface BoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface BoundaryState {
  hasError: boolean;
}

// Single-use error boundary: a render-time exception inside ReactMarkdown
// surfaces the "Note couldn't render" fallback instead of unmounting the
// whole spread.
class MarkdownErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Plan 11+ may forward to a real telemetry sink; Phase 6 just logs.
    console.error("StaticNoteRenderer: markdown render failed", error, info);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface ToneConfig {
  Icon: typeof Info | typeof AlertTriangle | typeof StickyNote;
  ariaLabel: string;
  containerClass: string;
  iconClass: string;
}

const TONES: Record<"info" | "warning" | "note", ToneConfig> = {
  info: {
    Icon: Info,
    ariaLabel: "Note",
    containerClass: "border-[var(--recipe-accent)] bg-[var(--recipe-accent-soft)]",
    iconClass: "text-[var(--recipe-accent)]",
  },
  warning: {
    Icon: AlertTriangle,
    ariaLabel: "Warning",
    containerClass: "border-destructive bg-destructive/10",
    iconClass: "text-destructive",
  },
  note: {
    Icon: StickyNote,
    ariaLabel: "Note",
    containerClass: "border-[var(--recipe-line)] bg-[var(--recipe-card)]",
    iconClass: "text-[var(--recipe-muted)]",
  },
};

export function StaticNoteRenderer({
  config,
  recipe,
}: WidgetRendererProps<"static-note">): React.ReactElement | null {
  // Read the FULL widget-states map (DR-2 / Pitfall 4): the gating constraint's
  // `when.ref` resolves a DIFFERENT widget's state, not this note's own. Mirrors
  // summary-checklist/renderer.tsx:73-78 — same null-guard throw so a renderer
  // mounted outside the provider fails loudly instead of silently un-gating.
  const ctx = useContext(RecipeStateContext);
  if (ctx === null) {
    throw new Error("<StaticNoteRenderer> must be rendered inside <RecipeStateProvider>");
  }

  // OPTIONAL gate: ungated path (visibilityConstraintId absent) renders
  // unconditionally — byte-identical to the pre-11.6 behavior. When the field
  // is set, the LIFTED fail-OPEN isVisibleById decides: false → hide (return
  // null); true (incl. unresolved/drifted id → fail-OPEN) → render the note.
  if (
    config.visibilityConstraintId !== undefined &&
    isVisibleById(config.visibilityConstraintId, recipe, ctx.states) === false
  ) {
    return null;
  }

  const tone = TONES[config.tone];
  const { Icon, ariaLabel, containerClass, iconClass } = tone;

  return (
    <div
      role="note"
      aria-label={ariaLabel}
      // The note APPEARS/DISAPPEARS based on state when gated, so the container
      // is an aria-live region (UI-SPEC §Accessibility item 3, Mission Critical)
      // — a screen-reader user hears the warning requirement appear after a
      // selection. role="note" alone does not announce the appearance event.
      // This is added to the container only; the tone classes are untouched.
      aria-live="polite"
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 text-[var(--recipe-ink)]",
        containerClass,
      )}
    >
      <Icon size={16} aria-hidden="true" className={iconClass} />
      <div className="flex-1">
        <MarkdownErrorBoundary
          fallback={<p className="text-sm text-[var(--recipe-muted)]">Note couldn't render</p>}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={COMPONENTS_ALLOWLIST}
            allowedElements={[...ALLOWED_ELEMENTS]}
            urlTransform={safeUrlTransform}
          >
            {config.body}
          </ReactMarkdown>
        </MarkdownErrorBoundary>
      </div>
    </div>
  );
}
